import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button, DatePicker, Drawer, Select, Space, notification,
} from 'antd';
import ActivityHistory from 'components/ActivityHistory/ActivityHistory';
import * as api from 'util/api.js';
import { errorMessage } from './helpers';

const { RangePicker } = DatePicker;

const PLATFORM_OPTIONS = [
  { value: 'matrix', label: 'Matrix' },
  { value: 'monday', label: 'Monday' },
  { value: 'cin7', label: 'Cin7' },
  { value: 'shopify', label: 'Shopify' },
  { value: 'kounta', label: 'Kounta' },
];

const STATUS_OPTIONS = [
  { value: 'submitted', label: 'Submitted / queued' },
  { value: 'applied', label: 'Confirmed applied' },
  { value: 'error', label: 'Error' },
];

/**
 * Cross-SKU price change ledger. Renders events with the SKU history timeline.
 */
export default function PriceChangeLedger({
  open,
  onClose,
  skuKey,
  refreshKey,
}) {
  const [range, setRange] = useState(null);
  const [platform, setPlatform] = useState(undefined);
  const [status, setStatus] = useState(undefined);

  const from = range?.[0] ? range[0].startOf('day').toISOString() : undefined;
  const to = range?.[1] ? range[1].endOf('day').toISOString() : undefined;

  const fetchPage = useCallback(({ before, limit }) => api.getPriceChangeLedger({
    from,
    to,
    platform,
    status,
    skuKey: skuKey != null ? skuKey : undefined,
    before,
    limit,
  }), [from, to, platform, status, skuKey]);

  const exportCsv = useCallback(() => {
    api.downloadPriceChangeLedgerCsv({
      from,
      to,
      platform,
      status,
      skuKey: skuKey != null ? skuKey : undefined,
    }).catch((err) => {
      notification.error({ message: errorMessage(err, 'Export failed') });
    });
  }, [from, to, platform, status, skuKey]);

  const historyKey = useMemo(
    () => [from, to, platform, status, skuKey, refreshKey].join('|'),
    [from, to, platform, status, skuKey, refreshKey],
  );

  return (
    <Drawer
      title={skuKey != null ? `Price change ledger · SKU ${skuKey}` : 'Price change ledger'}
      placement="right"
      width="min(720px, 96vw)"
      visible={open}
      onClose={onClose}
    >
      <Space wrap style={{ marginBottom: 16 }}>
        <RangePicker value={range} onChange={setRange} />
        <Select
          allowClear
          placeholder="All platforms"
          style={{ width: 150 }}
          value={platform}
          onChange={setPlatform}
          options={PLATFORM_OPTIONS}
        />
        <Select
          allowClear
          placeholder="All statuses"
          style={{ width: 170 }}
          value={status}
          onChange={setStatus}
          options={STATUS_OPTIONS}
        />
        <Button onClick={exportCsv}>Export CSV</Button>
      </Space>
      {open ? (
        <ActivityHistory
          key={historyKey}
          context="price_change"
          fetchPage={fetchPage}
          showActorFilter={false}
          compact
        />
      ) : null}
    </Drawer>
  );
}

PriceChangeLedger.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  skuKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  refreshKey: PropTypes.number,
};

PriceChangeLedger.defaultProps = {
  skuKey: null,
  refreshKey: 0,
};
